import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '@koridor/api-client';
import { useAuth } from './AuthContext';

interface FriendsContextValue {
  pendingRequestCount: number;
  refreshFriendRequests: () => Promise<unknown[]>;
}

const FriendsContext = createContext<FriendsContextValue | null>(null);

export function FriendsProvider({ children }: { children: React.ReactNode }) {
  const { user, isGuest, isOfflineSession } = useAuth();
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  const refreshFriendRequests = useCallback(async () => {
    if (!user || isGuest || isOfflineSession || !api.isAuthenticated) {
      setPendingRequestCount(0);
      return [];
    }
    try {
      const rows = await api.getFriendRequests();
      const list = rows ?? [];
      setPendingRequestCount(list.length);
      return list;
    } catch {
      setPendingRequestCount(0);
      return [];
    }
  }, [user, isGuest, isOfflineSession]);

  useEffect(() => {
    if (!user || isGuest || isOfflineSession) {
      setPendingRequestCount(0);
      return;
    }
    void refreshFriendRequests();
  }, [user, isGuest, isOfflineSession, refreshFriendRequests]);

  useEffect(() => {
    if (!user || isGuest || isOfflineSession) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshFriendRequests();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, isGuest, isOfflineSession, refreshFriendRequests]);

  useEffect(() => {
    if (!user || isGuest || isOfflineSession) return;
    const id = window.setInterval(() => { void refreshFriendRequests(); }, 60_000);
    return () => window.clearInterval(id);
  }, [user, isGuest, isOfflineSession, refreshFriendRequests]);

  return (
    <FriendsContext.Provider value={{ pendingRequestCount, refreshFriendRequests }}>
      {children}
    </FriendsContext.Provider>
  );
}

export function useFriends() {
  const ctx = useContext(FriendsContext);
  if (!ctx) throw new Error('useFriends outside provider');
  return ctx;
}
